import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiCheckbox, MiIcon } from "@miplatform/components";
import { ICONS_WEB, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { getCurrencyString } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { CollapsibleIcon } from "../../../../../components/CollapsibleIcon/CollapsibleIcon";

export function OptionCard({
  option,
  priceSummary,
  renderActions,
  renderPriceSummary,
  showOptionPreviews,
  handleSetShowPreview,
  previewUnavailable = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();

  const formatPrice = (value) => {
    const roundedPrice = (
      Math.round((value + Number.EPSILON) * 100) / 100
    ).toFixed(2);

    return roundedPrice < 0
      ? `(${getCurrencyString(Math.abs(roundedPrice))})`
      : `\u00A0${getCurrencyString(roundedPrice)}\u00A0`;
  };

  const getPreviewBgColor = () => {
    if (option?.showPreview) {
      return theme.palette.green.light;
    }
    if (previewUnavailable) {
      return theme.palette.grey.lightAccent;
    }
    return theme.palette.common.white;
  };
  const { classes } = useStyles({ bgColor: getPreviewBgColor() });

  const drawCard = ({ draw, value }) => {
    const fontColor =
      value >= 0 ? theme.palette.success.main : theme.palette.error.main;
    const hasValue = value !== undefined && value !== null;

    return (
      <Box key={draw + option.id} className={classes.drawCard}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.CAPTION}
          fontWeight={theme.fontWeight.semiBold}
        >
          {`${draw}: `}
        </Typography>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.CAPTION}
          fontWeight={theme.fontWeight.semiBold}
          color={hasValue ? fontColor : undefined}
        >
          {hasValue ? `${formatPrice(value)}` : "--"}
        </Typography>
      </Box>
    );
  };

  return (
    <Box key={option?.id} className={classes.root}>
      <Box display="flex" flex={1} alignItems="center">
        <Collapse
          in={showOptionPreviews}
          timeout={150}
          unmountOnExit
          orientation="horizontal"
        >
          <MiCheckbox
            name={option?.name}
            style={{
              marginRight: theme.spacing(1),
            }}
            value={option?.showPreview}
            handleChange={(e) => {
              handleSetShowPreview(option.id, e.target.checked);
            }}
            disabled={previewUnavailable}
          />
        </Collapse>
        <Box className={classes.cardIconContainer}>
          <Typography fontSize={theme.fontSize.small}>STRUCTURAL</Typography>
          <MiIcon path={mdi[ICONS_WEB.WALL]} size={0.8} />
          <Typography fontSize={theme.fontSize.small}>OPTION</Typography>
        </Box>
        <Box className={classes.cardContentContainer}>
          <Box className={classes.cardTitle}>
            <Typography
              fontWeight={theme.fontWeight.semiBold}
              variant={TYPOGRAPHY_VARIANTS.BODY2}
              color={previewUnavailable && theme.palette.grey.medium}
            >
              {option?.name}
            </Typography>
            {previewUnavailable && (
              <Box display="flex" gap={1} alignItems="center">
                <Typography
                  fontWeight={theme.fontWeight.semiBold}
                  variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
                  color={theme.palette.red.main}
                >
                  Unavailable
                </Typography>
                <MiIcon
                  path={mdi[ICONS_WEB.INFORMATION_OUTLINE]}
                  size={0.8}
                  color={theme.palette.red.main}
                />
              </Box>
            )}
          </Box>
          {priceSummary ? (
            <Box className={classes.drawsContainer}>
              {priceSummary?.draws?.map((draw) => drawCard(draw))}
              {drawCard({ draw: "Total", value: priceSummary?.total })}
            </Box>
          ) : null}
        </Box>
        {renderActions()}
        <Box height={24} paddingLeft={theme.spacing(1)}>
          <CollapsibleIcon
            isCollapsed={isExpanded}
            onCollapse={() => setIsExpanded((prev) => !prev)}
          />
        </Box>
      </Box>
      <Collapse
        in={isExpanded}
        orientation="vertical"
        timeout={300}
        unmountOnExit
      >
        {renderPriceSummary()}
      </Collapse>
    </Box>
  );
}

const useStyles = makeStyles()((theme, { bgColor }) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    gap: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 8,
    boxShadow: `1px 1px 1px ${theme.palette.grey.accent}`,
    backgroundColor: bgColor,
    "&:hover": {
      boxShadow: `1px 1px 3px ${theme.palette.grey.accent}`,
    },
    transition: "box-shadow 0.2s ease-in-out",
  },
  cardIconContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 58,
    minWidth: 80,
    borderRadius: 8,
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.main,
    padding: theme.spacing(0, 2),
  },
  cardContentContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flex: 1,
    margin: theme.spacing(0, 2),
  },
  cardTitle: {
    display: "flex",
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  drawsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  drawCard: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "fit-content",
    maxWidth: "100%",
    backgroundColor: theme.palette.grey.light,
    borderRadius: 6,
    padding: theme.spacing(1, 3),
    gap: theme.spacing(1),
  },
}));
